import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import TotalModalWrapper from '../../Share/Normal/TotalModalWrapper';
import FetchModule from '../../Share/Network/FetchModule';

export default function WaitingProductLinkoPaymentModal(props) {
  const { userData, productData, onComplete, closeFn } = props;

  const history = useHistory();
  const { t } = useLanguage();

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [selectedProduct, setSelectedProduct] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);

  // 초기 상품 선택: userData.itemName과 일치하는 상품 //
  useEffect(() => {
    if (!productData || productData.length === 0) return;

    const matched = productData.find(p => p.name === userData.itemName);

    if (matched) setSelectedProduct(matched);
  }, [productData, userData]);

  // 포인트 사용 금액 //
  function getPointAmount() {
    return userData.point ?? 0;
  }

  // 최종 결제 금액 //
  function calcFinalPrice() {
    if (!selectedProduct) return 0;

    const totalPrice = selectedProduct.price * (userData.quantity || 1);

    return Math.max(totalPrice - getPointAmount(), 0);
  }

  // 링코 페이 승인 클릭시 //
  function onClickApprove() {
    if (!selectedProduct) { alert(t('waiting.productLinkoPayModal.alertProductNotFound')); return; }
    
    if (isLoading) return;

    const storeId = window.sessionStorage.getItem('StoreId');

    const requestData = {
      id: -1,
      storeId: parseInt(storeId),
      memberId: parseInt(userData.memberId),
      productId: selectedProduct.id,
      merchantUid: userData.merchantUid,
      cardPrice: calcFinalPrice(),
      cashPrice: 0,
      discountPrice: 0,
      notReceivedPrice: 0,
      point: getPointAmount(),
      count: userData.quantity || 1,
      approvalDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
    };

    setIsLoading(true);
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('product/usage/approve/' + userData.merchantUid, 'POST', requestData);

        if (responseData.status === 200) {
          alert(t('waiting.productPaymentModal.alertPurchaseComplete'));
          setIsLoading(false);
          if (onComplete) onComplete();
        }
        else if (responseData.status === 401) {
          alert(t('waiting.productPaymentModal.alertLoginRequired'));
          history.push('/login');
        }
        else {
          setIsLoading(false);
        }
      }
    )();
  }

  if (!userData) return null;

  const productPicture = selectedProduct
    ? (selectedProduct.picture || 'https://picture.link-zone.org/DefaultLogo.png')
    : (userData.itemPicture || 'https://picture.link-zone.org/DefaultLogo.png');

  const count = userData.quantity || 1;
  const totalPrice = selectedProduct ? selectedProduct.price * count : 0;
  const pointAmount = getPointAmount();
  const finalPrice = calcFinalPrice();
  const approvalDate = moment(userData.createdDate).format(t('calendarInput.rangeDateFormat'));

  return (
    <TotalModalWrapper>
      <ModalView>
        <HeaderRow>
          <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text}>{userData.userName} - {t('waiting.productPaymentModal.titleSuffix')}</Text>
        </HeaderRow>
        <ContentRow>
          <ProductImage src={productPicture} alt='product' />
          <InfoWrapper>
            <InfoRow>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{selectedProduct ? selectedProduct.name : userData.itemName}</Text>
              <CountWrapper>
                <TextWrapper width={85}>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{count}</Text>
                </TextWrapper>
                <CountUnitBox>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('waiting.productLinkoPayModal.countUnit')}</Text>
                </CountUnitBox>
              </CountWrapper>
            </InfoRow>
            <InfoRow>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('waiting.productLinkoPayModal.paymentAmount')}</Text>
              <ValueUnitWrapper>
                <ValueBox background={theme.background}>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{totalPrice.toLocaleString('ko-KR')}</Text>
                </ValueBox>
                <UnitBox>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('waiting.productPaymentModal.wonUnit')}</Text>
                </UnitBox>
              </ValueUnitWrapper>
            </InfoRow>
            <InfoRow>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('waiting.onSitePaymentModal.pointUsageAmount')}</Text>
              <ValueUnitWrapper>
                <ValueBox background={theme.background}>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subKey}>- {pointAmount.toLocaleString('ko-KR')}</Text>
                </ValueBox>
                <UnitBox>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subKey}>point</Text>
                </UnitBox>
              </ValueUnitWrapper>
            </InfoRow>
            <InfoRow>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('waiting.onSitePaymentModal.finalPaymentAmount')}</Text>
              <ValueUnitWrapper>
                <ValueBox background={theme.background}>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{finalPrice.toLocaleString('ko-KR')}</Text>
                </ValueBox>
                <UnitBox>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('waiting.productPaymentModal.wonUnit')}</Text>
                </UnitBox>
              </ValueUnitWrapper>
            </InfoRow>
            <InfoRow>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('waiting.onSitePaymentModal.paymentRequestDate')}</Text>
              <ValueUnitWrapper>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{approvalDate}</Text>
              </ValueUnitWrapper>
            </InfoRow>
          </InfoWrapper>
        </ContentRow>
        <FooterRow>
          <CancelButton onClick={closeFn}>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('waiting.productPaymentModal.cancel')}</Text>
          </CancelButton>
          <ApproveButton onClick={onClickApprove} disabled={isLoading}>
            <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.subBackground}>{t('waiting.productLinkoPayModal.approveButton')}</Text>
          </ApproveButton>
        </FooterRow>
      </ModalView>
    </TotalModalWrapper>
  );
}

const ModalView = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  padding: 20px;

  width: 463px;

  gap: 10px;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  margin-bottom: 30px;

  width: 100%;
`;

const ContentRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  width: 100%;

  gap: 20px;
`;

const ProductImage = styled.img`
  width: 181px;
  height: 181px;

  border-radius: 10px;
  object-fit: cover;

  flex-shrink: 0;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;
  height: 181px;

  gap: 5px;

  border-radius: 10px;
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
  height: 32px;
`;

const CountWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  width: 103px;
  height: 32px;

  gap: 5px;
`;

const CountUnitBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  height: 32px;
`;

const ValueUnitWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  gap: 5px;
`;

const ValueBox = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  padding: 0 10px;

  width: 85px;
  height: 32px;

  background: ${props => props.background || 'transparent'};
  border-radius: 5px;

  box-sizing: border-box;
`;

const UnitBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 35px;
  height: 32px;

  border-radius: 5px;
`;

const FooterRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  padding-top: 10px;

  width: 100%;

  gap: 10px;
`;

const CancelButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 11.5px 32px;

  height: 40px;

  background: ${props => props.theme.buttonFill};
  border-radius: 10px;

  box-sizing: border-box;

  cursor: pointer;
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: ${props => props.theme.line};
  }
`;

const ApproveButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 5px 16px;

  flex: 1;
  height: 40px;

  background: ${props => props.disabled ? props.theme.line : props.theme.subKey};
  border-radius: 10px;

  box-sizing: border-box;

  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      background-color: ${props => props.disabled ? props.theme.line : props.theme.subKeyHover};
    }
  }
`;

const TextWrapper = styled.div`
  display: flex;

  justify-content: flex-end;
  align-items: center;

  width: ${props => props.width ? `${props.width}px` : '100%'};
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0 0 0 0;

  color: ${props => props.color};

  &:hover {
    cursor: ${props => props.cursor};
  }

  transition: all 0.3s ease-in-out;
`;
